/* jshint esversion: 6 */
import { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import secureLocalStorage from "react-secure-storage";
import { MyContext } from "../../Context/MyProvider";
import { showToast } from "../../showTost";

const HYDERABAD = { latitude: 17.479, longitude: 78.588, address: "Hyderabad, Telangana" };

/* Client-side categories — the medicines API has no category field,
   so we map generic/name keywords to a category. Unknown → "General". */
const CATEGORY_RULES = [
  { name: "Pain & Fever", keys: ["paracetamol", "crocin", "dolo", "ibuprofen", "diclofenac", "aceclofenac", "aspirin", "meftal", "combiflam", "tramadol", "pain", "fever", "headache"] },
  { name: "Antibiotics", keys: ["azithromycin", "amoxicillin", "cef", "ciprofloxacin", "doxycycline", "metronidazole", "antibiotic", "azee", "moxikind"] },
  { name: "Allergy, Cold & Cough", keys: ["cetirizine", "levocetirizine", "loratadine", "fexofenadine", "montelukast", "cough", "cold", "allegra", "cetzine", "ascoril", "benadryl", "sneeze"] },
  { name: "Digestive Care", keys: ["omeprazole", "pantoprazole", "antacid", "digene", "eno", "acidity", "gas", "constipation", "diarrhoea", "diarrhea", "ors", "vomit"] },
  { name: "Diabetes Care", keys: ["metformin", "insulin", "glimepiride", "glycomet", "diabetes", "glucose", "sugar"] },
  { name: "Heart & BP", keys: ["atorvastatin", "amlodipine", "telmisartan", "losartan", "cholesterol", "ecospirin", "blood pressure", " cardiac", "heart"] },
  { name: "Vitamins & Supplements", keys: ["vitamin", "multivitamin", "calcium", "iron", "zinc", "supplement", "shelcal", "becosules", "protein"] },
  { name: "Skin Care", keys: ["cream", "ointment", "lotion", "skin", "candid", "betadine", "rash", "antifungal"] },
  { name: "Eye & Ear", keys: ["eye ", "eye", "ear ", "ear", "drop"] },
  { name: "Baby & Mother Care", keys: ["baby", "mother", "pregnancy", "lactation", "pediasure", "diaper"] },
  { name: "Devices & Daily Care", keys: ["wheel", "chair", "device", "bandage", "syringe", "mask", "sanitizer", "thermometer", "monitor", "mobility", "support", "belt", "walker", "stretcher"] },
];

const categorize = (m) => {
  const text = `${m.name || ""} ${m.generic_name || ""} ${m.form || ""}`.toLowerCase();
  for (const rule of CATEGORY_RULES) {
    if (rule.keys.some((k) => text.includes(k))) return rule.name;
  }
  return "General";
};

/* Broad single-letter queries whose union covers the full catalogue
   (the search API needs a non-empty query and has no list-all endpoint). */
const SEED_QUERIES = ["a", "e", "i", "o", "u"];

export default function PharmacySearch() {
  const { ApiBaseUrl } = useContext(MyContext);
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [catalogue, setCatalogue] = useState([]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [activeCategory, setActiveCategory] = useState("All");

  const userLoc = () => ({
    latitude: Number(secureLocalStorage.getItem("lat")) || HYDERABAD.latitude,
    longitude: Number(secureLocalStorage.getItem("lon")) || HYDERABAD.longitude,
    address: HYDERABAD.address,
  });

  const mergeMedicines = (lists) => {
    setCatalogue((prev) => {
      const map = new Map(prev.map((m) => [m.medicine_id, m]));
      lists.flat().forEach((m) => {
        if (m && m.medicine_id != null) map.set(m.medicine_id, m);
      });
      return [...map.values()];
    });
  };

  const searchApi = (q) =>
    axios.post(`${ApiBaseUrl}/api/v1/pharmacy/medicines/search`, {
      query: q,
      user_location: userLoc(),
      radius_km: 5,
    }).then((res) => (res.data.success ? res.data.medicines || [] : []));

  // Load the complete catalogue on page open.
  useEffect(() => {
    setLoading(true);
    Promise.allSettled(SEED_QUERIES.map(searchApi))
      .then((results) => {
        mergeMedicines(results.filter((r) => r.status === "fulfilled").map((r) => r.value));
      })
      .catch(() => showToast({ title: "Could not load medicines", id: "error" }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const search = () => {
    const q = query.trim();
    if (!q) return;
    setSearching(true);
    searchApi(q)
      .then((list) => {
        if (!list.length) showToast({ title: "No results", id: "info" });
        mergeMedicines([list]);
      })
      .catch(() => showToast({ title: "Search failed", id: "error" }))
      .finally(() => setSearching(false));
  };

  const categories = useMemo(() => {
    const counts = {};
    catalogue.forEach((m) => {
      const c = categorize(m);
      counts[c] = (counts[c] || 0) + 1;
    });
    const ordered = CATEGORY_RULES.map((r) => r.name).filter((n) => counts[n]);
    if (counts.General) ordered.push("General");
    return [{ name: "All", count: catalogue.length }, ...ordered.map((n) => ({ name: n, count: counts[n] }))];
  }, [catalogue]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return catalogue.filter((m) => {
      if (activeCategory !== "All" && categorize(m) !== activeCategory) return false;
      if (!q) return true;
      return `${m.name || ""} ${m.generic_name || ""}`.toLowerCase().includes(q);
    });
  }, [catalogue, activeCategory, query]);

  const getQty = (medicine_id) => {
    const ex = cart.find((x) => x.medicine_id === medicine_id);
    return ex ? ex.quantity : 0;
  };

  const addToCart = (m) => {
    setCart((c) => {
      const ex = c.find((x) => x.medicine_id === m.medicine_id);
      if (ex) return c.map((x) => x.medicine_id === m.medicine_id ? { ...x, quantity: x.quantity + 1 } : x);
      return [...c, { medicine_id: m.medicine_id, name: m.name, quantity: 1, requires_prescription: m.requires_prescription }];
    });
  };

  const increment = (medicine_id) => {
    setCart((c) => c.map((x) => x.medicine_id === medicine_id ? { ...x, quantity: x.quantity + 1 } : x));
  };

  const decrement = (medicine_id) => {
    setCart((c) => {
      const ex = c.find((x) => x.medicine_id === medicine_id);
      if (!ex) return c;
      if (ex.quantity <= 1) return c.filter((x) => x.medicine_id !== medicine_id);
      return c.map((x) => x.medicine_id === medicine_id ? { ...x, quantity: x.quantity - 1 } : x);
    });
  };

  const clearCart = () => setCart([]);

  const totalQty = cart.reduce((sum, x) => sum + (x.quantity || 0), 0);

  const chooseSlot = () => {
    if (!cart.length) {
      showToast({ title: "Select at least one medicine", id: "warning" });
      return;
    }
    sessionStorage.setItem("pharmacy_cart", JSON.stringify(cart));
    navigate("/pharmacy/slots");
  };

  return (
    <div className="width-container py-8 pb-28">
      <p className="text-sm text-rm-primary font-medium mb-2">Hyderabad & Secunderabad · Partner pharmacies shown as RM-PHARM IDs only</p>
      <h1 className="text-2xl font-bold mb-4">Pharmacy delivery</h1>
      <p className="text-gray-600 mb-4">Browse all medicines below or search by name or generic. Add multiple medicines with quantities — we auto-match the nearest store with stock. No need to pick a pharmacy.</p>
      <div className="flex gap-2 mb-5">
        <input className="flex-1 border rounded-lg px-4 py-3" placeholder="Search paracetamol, crocin…" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} />
        <button type="button" className="rm-btn-primary px-6" onClick={search} disabled={searching}>{searching ? "…" : "Search"}</button>
      </div>

      {/* Categories */}
      <div className="flex gap-2 overflow-x-auto pb-3 mb-6 -mx-1 px-1">
        {categories.map((c) => (
          <button
            key={c.name}
            type="button"
            onClick={() => setActiveCategory(c.name)}
            className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition ${
              activeCategory === c.name
                ? "bg-rm-primary text-white border-rm-primary"
                : "bg-white text-gray-700 border-gray-200 hover:border-rm-primary"
            }`}
          >
            {c.name} <span className={`ml-1 text-xs ${activeCategory === c.name ? "text-white/80" : "text-gray-400"}`}>({c.count})</span>
          </button>
        ))}
      </div>

      {/* All medicines, filtered by the selected category */}
      {loading ? (
        <p className="text-gray-500 py-8 text-center">Loading all medicines…</p>
      ) : visible.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p className="font-medium">No medicines in {activeCategory === "All" ? "this search" : activeCategory}</p>
          <button type="button" className="mt-3 text-sm text-rm-primary underline" onClick={() => { setActiveCategory("All"); setQuery(""); }}>
            Show all medicines
          </button>
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-500 mb-3">Showing {visible.length} of {catalogue.length} medicine(s){activeCategory !== "All" ? ` in ${activeCategory}` : ""}</p>
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {visible.map((m) => {
              const qty = getQty(m.medicine_id);
              const selected = qty > 0;
              return (
                <div key={m.medicine_id} className={`border rounded-xl p-4 bg-white shadow-sm ${selected ? "ring-2 ring-rm-primary" : ""}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="font-semibold">{m.name}</h3>
                      <p className="text-sm text-gray-500">{m.generic_name}{m.strength ? ` · ${m.strength}` : ""}{m.form ? ` · ${m.form}` : ""}</p>
                    </div>
                    <span className="flex-shrink-0 text-[11px] px-2 py-1 rounded-full bg-gray-100 text-gray-600">{categorize(m)}</span>
                  </div>
                  <p className="text-rm-primary font-bold mt-2">₹{m.availability?.min_price?.toFixed(0)} – ₹{m.availability?.max_price?.toFixed(0)}</p>
                  <p className="text-xs text-gray-500">ETA ~{m.delivery?.nearest_store_eta_min} min · {m.availability?.stores_in_stock} stores nearby</p>
                  {m.requires_prescription && <span className="text-xs text-amber-700">Prescription required</span>}
                  {qty === 0 ? (
                    <button type="button" className="mt-3 text-sm bg-rm-primary text-white px-4 py-2 rounded-lg" onClick={() => addToCart(m)}>Add</button>
                  ) : (
                    <div className="mt-3 flex items-center gap-3">
                      <button type="button" className="w-8 h-8 rounded-lg border font-bold" onClick={() => decrement(m.medicine_id)} aria-label="Decrease quantity">−</button>
                      <span className="font-semibold min-w-[24px] text-center">{qty}</span>
                      <button type="button" className="w-8 h-8 rounded-lg border font-bold" onClick={() => increment(m.medicine_id)} aria-label="Increase quantity">+</button>
                      <span className="text-xs text-gray-500">in cart</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {cart.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 shadow-lg z-[1300]">
          <div className="width-container flex justify-between items-center gap-4">
            <div>
              <span className="font-medium">{cart.length} medicine(s) · Qty {totalQty}</span>
              <button type="button" className="ml-3 text-xs text-gray-500 underline" onClick={clearCart}>Clear</button>
            </div>
            <button type="button" className="rm-btn-primary px-6 py-3 min-w-[140px]" onClick={chooseSlot}>Choose slot</button>
          </div>
        </div>
      )}
      <p className="text-sm mt-4">First booking? Use code <strong>WELCOME10</strong> at checkout. Secure payment options are shown at payment.</p>
      <Link to="/shop" className="text-sm text-gray-500 underline mt-2 inline-block">Legacy store browse</Link>
    </div>
  );
}